import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle, XCircle, Bus, Users, LogOut, Navigation, Calendar, Trash2, ArrowLeft, Shield, Lock, Map, HelpCircle, CalendarOff, Activity, X, Megaphone, ClipboardList } from 'lucide-react';
import AdminRouteHistory from './AdminRouteHistory';
import CounselorHelp from './CounselorHelp';
import LiveAttendanceDashboard from './LiveAttendanceDashboard';
import AttendanceAuditLog from './AttendanceAuditLog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_VERSION = "v2.9";
const ADMIN_PIN = "admin";
const ADMIN_PASSWORD = "Camp1993";

function getTodayEST() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

function isAttendanceClosed() {
  const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit' });
  const [h, m] = now.split(':').map(Number);
  return h > 9 || (h === 9 && m >= 30);
}

export default function CounselorApp() {
  // ----- Persisted session restore (so manual refresh / reset doesn't kick the
  // user back to the login screen on network blips). We hydrate from localStorage
  // synchronously so the app shell renders with the cached state immediately,
  // then refresh in the background.
  const _savedAdmin = (typeof window !== 'undefined') && localStorage.getItem('counselor_admin_mode') === 'true';
  const _savedBusData = (() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem('counselor_bus_data');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const _savedBus = (typeof window !== 'undefined') ? localStorage.getItem('counselor_bus') : null;

  const [isLoggedIn, setIsLoggedIn] = useState(Boolean(_savedAdmin || _savedBusData || _savedBus));
  const [showHelp, setShowHelp] = useState(false);
  const [isAdmin, setIsAdmin] = useState(Boolean(_savedAdmin));
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busData, setBusData] = useState(_savedBusData);
  const [attendance, setAttendance] = useState({});
  const [selectedDate, setSelectedDate] = useState(getTodayEST());
  const [gpsStatus, setGpsStatus] = useState('idle');
  const [gpsMessage, setGpsMessage] = useState('');
  const [locationCount, setLocationCount] = useState(0);
  const [wakeLockActive, setWakeLockActive] = useState(false);
  const [activePeriod, setActivePeriod] = useState(() => {
    const h = parseInt(new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit' }));
    return h < 12 ? 'am' : 'pm';
  });
  const [attendanceClosed, setAttendanceClosed] = useState(isAttendanceClosed());
  const [attendanceUnlocked, setAttendanceUnlocked] = useState(false);
  // Admin state
  const [adminDates, setAdminDates] = useState([]);
  const [adminBus, setAdminBus] = useState('');
  const [adminResult, setAdminResult] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [adminPasswordInput, setAdminPasswordInput] = useState('');
  const [adminPasswordError, setAdminPasswordError] = useState('');
  const [adminAuthenticated, setAdminAuthenticated] = useState(_savedAdmin);
  const [adminTab, setAdminTab] = useState('live'); // 'live' | 'routes' | 'clear' | 'absences' | 'broadcast'
  // Broadcast state
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastUrgent, setBroadcastUrgent] = useState(false);
  const [broadcastList, setBroadcastList] = useState([]);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastFeedback, setBroadcastFeedback] = useState(null);
  // Counselor-facing broadcast banner state
  const [activeBroadcasts, setActiveBroadcasts] = useState([]);
  const [dismissedBroadcastIds, setDismissedBroadcastIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('dismissedBroadcasts') || '[]')); }
    catch { return new Set(); }
  });
  // Planned Absences state
  const [absencesList, setAbsencesList] = useState([]);
  const [absencesLoading, setAbsencesLoading] = useState(false);
  const [absenceCamperQuery, setAbsenceCamperQuery] = useState('');
  const [absenceSelectedCamper, setAbsenceSelectedCamper] = useState(null);
  const [absenceStartDate, setAbsenceStartDate] = useState('');
  const [absenceEndDate, setAbsenceEndDate] = useState('');
  const [absenceMessage, setAbsenceMessage] = useState(null);
  const [allCampers, setAllCampers] = useState([]);
  
  const gpsIntervalRef = useRef(null);
  const watchIdRef = useRef(null);
  const busNumberRef = useRef(null);
  const wakeLockRef = useRef(null);
  const lastSentRef = useRef(null);
  const rosterPollRef = useRef(null);
  // Sticky map of every camper we have ever seen on this bus this session.
  // Used to keep a locally-marked kid visible even if CamperSnapshot drops
  // them from the roster after an Absent push (so accidental taps can be
  // corrected).
  const seenCampersRef = useRef({});
  const [liveRoster, setLiveRoster] = useState(null);
  const [lastPollTime, setLastPollTime] = useState(null);

  // Ensure scrolling works on mobile - remove any overflow restrictions from parents
  useEffect(() => {
    const rootEl = document.getElementById('root');
    const appEl = document.querySelector('.App');
    // Remove any fixed/overflow constraints that could trap scrolling
    [document.body, document.documentElement, rootEl, appEl].forEach(el => {
      if (el) {
        el.style.overflow = 'visible';
        el.style.height = 'auto';
        el.style.position = 'static';
      }
    });
    return () => {
      [document.body, document.documentElement, rootEl, appEl].forEach(el => {
        if (el) {
          el.style.overflow = '';
          el.style.height = '';
          el.style.position = '';
        }
      });
    };
  }, []);

  // Check attendance cutoff every 30s
  useEffect(() => {
    const timer = setInterval(() => setAttendanceClosed(isAttendanceClosed()), 30000);
    return () => clearInterval(timer);
  }, []);

  const requestWakeLock = async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        setWakeLockActive(true);
        wakeLockRef.current.addEventListener('release', () => setWakeLockActive(false));
      } catch (err) {
        setWakeLockActive(false);
      }
    }
  };

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && isLoggedIn && !wakeLockRef.current) {
        requestWakeLock();
        if (busNumberRef.current && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(sendLocationUpdate, () => {}, { enableHighAccuracy: true, timeout: 10000 });
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isLoggedIn]);

  useEffect(() => {
    return () => { if (wakeLockRef.current) wakeLockRef.current.release(); };
  }, []);

  useEffect(() => {
    const savedBus = localStorage.getItem('counselor_bus');
    const savedAdmin = localStorage.getItem('counselor_admin_mode') === 'true';
    if (savedAdmin) {
      // Admin session restored from localStorage — no API call needed; admin
      // password gate is already passed (adminAuthenticated initialised true).
      return;
    }
    if (savedBus) {
      // If we hydrated busData from cache, restore the bus number ref so
      // markAttendance / poll have it immediately. GPS + wake lock start once
      // handleAutoLogin succeeds (avoids duplicate watchPosition handlers).
      if (_savedBusData?.bus_number) {
        busNumberRef.current = _savedBusData.bus_number;
      }
      handleAutoLogin(savedBus);
    }
  }, []);

  const handleAutoLogin = async (savedPin) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bus-tracking/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: savedPin })
      });
      if (response.ok) {
        const data = await response.json();
        setBusData(data); setAttendance(data.attendance || {}); setIsLoggedIn(true);
        busNumberRef.current = data.bus_number;
        // Cache for next manual reset / hard refresh.
        try { localStorage.setItem('counselor_bus_data', JSON.stringify(data)); } catch { /* quota */ }
        await requestWakeLock(); startGpsTracking();
      } else if (response.status === 401 || response.status === 403 || response.status === 404) {
        // PIN is definitively invalid — clear saved login.
        localStorage.removeItem('counselor_bus');
        localStorage.removeItem('counselor_bus_data');
        setIsLoggedIn(false); setBusData(null); busNumberRef.current = null;
      }
      // Any other status (5xx, 408, 429, Cloudflare 520, etc.) is treated as
      // transient: keep the saved login and the cached busData so the user
      // stays in the app shell. We'll retry on the next reset or via polling.
    } catch {
      // Network error — DO NOT clear saved login. The cached busData (if any)
      // keeps the bus user inside the app; a refresh / interaction triggers retry.
    }
    finally { setLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    // Admin mode - show password prompt
    if (pin.toLowerCase() === ADMIN_PIN) {
      setIsAdmin(true); setIsLoggedIn(true); setAdminAuthenticated(false); setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/bus-tracking/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Login failed. Try again.');
      }
      const data = await response.json();
      localStorage.setItem('counselor_bus', pin);
      try { localStorage.setItem('counselor_bus_data', JSON.stringify(data)); } catch { /* quota */ }
      setBusData(data); setAttendance(data.attendance || {}); setIsLoggedIn(true);
      busNumberRef.current = data.bus_number;
      await requestWakeLock(); startGpsTracking();
    } catch (err) { setError(err.message === 'Failed to fetch' ? 'Connection error. Check your internet and try again.' : err.message); }
    finally { setLoading(false); }
  };

  const sendLocationUpdate = useCallback(async (position) => {
    const busNumber = busNumberRef.current;
    if (!busNumber) return;
    const now = Date.now();
    if (lastSentRef.current && now - lastSentRef.current < 5000) return;
    lastSentRef.current = now;
    try {
      const response = await fetch(`${API_URL}/api/bus-tracking/location`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bus_number: busNumber, latitude: position.coords.latitude, longitude: position.coords.longitude,
          accuracy: position.coords.accuracy, speed: position.coords.speed, heading: position.coords.heading
        })
      });
      if (response.ok) {
        setLocationCount(prev => prev + 1);
        setGpsMessage(`Sent! ${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}`);
        setGpsStatus('tracking');
      } else { setGpsMessage(`Server error: ${response.status}`); }
    } catch (err) { setGpsMessage(`Network error: ${err.message}`); }
  }, []);

  const startGpsTracking = useCallback(() => {
    setGpsStatus('requesting'); setGpsMessage('Getting GPS...');
    if (!navigator.geolocation) { setGpsStatus('error'); setGpsMessage('GPS not supported'); return; }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsStatus('tracking'); setGpsMessage(''); sendLocationUpdate(position);
        watchIdRef.current = navigator.geolocation.watchPosition(sendLocationUpdate, () => {}, { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 });
        gpsIntervalRef.current = setInterval(() => {
          navigator.geolocation.getCurrentPosition(sendLocationUpdate, () => {}, { enableHighAccuracy: true, timeout: 20000, maximumAge: 15000 });
        }, 15000);
      },
      (err) => { setGpsStatus('error'); setGpsMessage(err.code === 1 ? 'Location permission denied' : 'GPS unavailable'); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [sendLocationUpdate]);

  const stopGpsTracking = useCallback(() => {
    if (watchIdRef.current !== null) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
    if (gpsIntervalRef.current) { clearInterval(gpsIntervalRef.current); gpsIntervalRef.current = null; }
    if (wakeLockRef.current) { wakeLockRef.current.release(); wakeLockRef.current = null; }
  }, []);

  // Roster polling — fetch live roster from CamperSnapshot every 30s.
  // Bulletproof fetch: retries on network errors, 502, 503, 520, 522, 524, 408, 429.
  // Total budget ~12s across 4 attempts with exponential backoff + jitter.
  const fetchWithRetry = useCallback(async (url, options = {}, label = '') => {
    const RETRY_STATUS = new Set([408, 425, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524]);
    const MAX_ATTEMPTS = 4;
    let lastErr;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      try {
        const controller = new AbortController();
        const tid = setTimeout(() => controller.abort(), 25000); // 25s per attempt
        const res = await fetch(url, { ...options, signal: controller.signal });
        clearTimeout(tid);
        if (res.ok) return res;
        if (!RETRY_STATUS.has(res.status)) return res; // non-retriable client error → return as-is
        lastErr = new Error(`${label || url} HTTP ${res.status}`);
      } catch (e) {
        lastErr = e;
      }
      if (attempt < MAX_ATTEMPTS - 1) {
        // Exponential backoff with jitter: 500ms, 1500ms, 3500ms
        const base = 500 * Math.pow(2, attempt);
        const jitter = Math.random() * 500;
        await new Promise(r => setTimeout(r, base + jitter));
      }
    }
    throw lastErr || new Error('fetchWithRetry failed');
  }, []);

  // Uses recursive setTimeout (more resilient than setInterval on iOS Safari which
  // throttles intervals heavily when screen is off / tab idle). Also re-polls on
  // visibility change, window focus, AND on any user touch/scroll/click if overdue.
  const pollRoster = useCallback(async () => {
    const bus = busNumberRef.current;
    if (!bus) return;
    try {
      const res = await fetchWithRetry(
        `${API_URL}/api/bus-tracking/live-roster/${encodeURIComponent(bus)}?period=${activePeriod}&date=${selectedDate}&_t=${Date.now()}`,
        { cache: 'no-store' },
        'live-roster'
      );
      const data = await res.json();
      if (data.success) {
        setLiveRoster(data);
        setLastPollTime(new Date());
        lastPollTsRef.current = Date.now();
        if (typeof data.attendance_unlocked === 'boolean') setAttendanceUnlocked(data.attendance_unlocked);
      }
    } catch { /* silent — next 30s poll will retry */ }
  }, [selectedDate, activePeriod, fetchWithRetry]);

  const lastPollTsRef = useRef(0);

  const startRosterPolling = useCallback(() => {
    if (rosterPollRef.current) clearTimeout(rosterPollRef.current);
    // Recursive setTimeout — survives iOS Safari throttling better than setInterval.
    const schedule = async () => {
      await pollRoster();
      rosterPollRef.current = setTimeout(schedule, 30000);
    };
    schedule();
  }, [pollRoster]);

  const stopRosterPolling = useCallback(() => {
    if (rosterPollRef.current) { clearTimeout(rosterPollRef.current); rosterPollRef.current = null; }
  }, []);

  // Start/stop polling on login/logout
  useEffect(() => {
    if (isLoggedIn && !isAdmin && busNumberRef.current) {
      startRosterPolling();
    }
    return () => stopRosterPolling();
  }, [isLoggedIn, isAdmin, startRosterPolling, stopRosterPolling]);

  // Poll active broadcasts every 30s while logged in (counselors AND admins see them).
  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API_URL}/api/broadcasts/active`, { cache: 'no-store' });
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setActiveBroadcasts(j.broadcasts || []);
      } catch { /* silent */ }
    };
    poll();
    const iv = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isLoggedIn]);

  const dismissBroadcastLocally = useCallback((id) => {
    setDismissedBroadcastIds(prev => {
      const next = new Set(prev);
      next.add(id);
      try { localStorage.setItem('dismissedBroadcasts', JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const visibleBroadcasts = React.useMemo(
    () => activeBroadcasts.filter(b => !dismissedBroadcastIds.has(b.id)),
    [activeBroadcasts, dismissedBroadcastIds]
  );

  // Catch-up poll on tab visibility, window focus, AND any user interaction if overdue.
  // iOS Safari may pause our timer when the screen is off; these handlers force a fresh
  // fetch the moment the counselor interacts with the app again.
  useEffect(() => {
    if (!isLoggedIn || isAdmin) return;
    const maybeCatchUp = () => {
      const overdue = Date.now() - lastPollTsRef.current > 30000;
      if (overdue) pollRoster();
    };
    const handleVis = () => { if (document.visibilityState === 'visible') maybeCatchUp(); };
    document.addEventListener('visibilitychange', handleVis);
    window.addEventListener('focus', maybeCatchUp);
    window.addEventListener('touchstart', maybeCatchUp, { passive: true });
    window.addEventListener('click', maybeCatchUp);
    return () => {
      document.removeEventListener('visibilitychange', handleVis);
      window.removeEventListener('focus', maybeCatchUp);
      window.removeEventListener('touchstart', maybeCatchUp);
      window.removeEventListener('click', maybeCatchUp);
    };
  }, [isLoggedIn, isAdmin, pollRoster]);

  const handleLogout = () => {
    stopGpsTracking(); stopRosterPolling();
    localStorage.removeItem('counselor_bus');
    localStorage.removeItem('counselor_bus_data');
    localStorage.removeItem('counselor_admin_mode');
    busNumberRef.current = null;
    setIsLoggedIn(false); setIsAdmin(false); setBusData(null); setAttendance({}); setPin(''); setLocationCount(0); setGpsStatus('idle'); setWakeLockActive(false);
    setSelectedDate(getTodayEST()); setAdminDates([]); setAdminBus(''); setAdminResult(null); setShowConfirm(false);
    setAdminPasswordInput(''); setAdminPasswordError(''); setAdminAuthenticated(false); setAdminTab('routes');
    setLiveRoster(null); setLastPollTime(null);
  };

  useEffect(() => () => stopGpsTracking(), [stopGpsTracking]);

  // Fetch attendance when date changes
  const fetchAttendanceForDate = useCallback(async (date) => {
    if (!busData?.bus_number) return;
    try {
      const res = await fetch(`${API_URL}/api/bus-tracking/attendance/${encodeURIComponent(busData.bus_number)}?date=${date}`);
      const data = await res.json();
      if (res.ok) {
        const attMap = {};
        (data.records || []).forEach(r => { attMap[r.camper_id] = r.status; });
        setAttendance(attMap);
      } else {
        setAttendance({});
      }
    } catch {
      setAttendance({});
    }
  }, [busData?.bus_number]);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
    setAttendance({});
    fetchAttendanceForDate(newDate);
    setLiveRoster(null); // clear stale roster, next poll will fetch for new date
  };

  const toggleAdminDate = (date) => {
    setAdminDates(prev => prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]);
  };

  // Unlock lives on the SERVER so every bus phone gets it (was localStorage —
  // which only unlocked the admin's own device; Bus #23 incident 7/28).
  const toggleAttendanceLock = async () => {
    const newVal = !attendanceUnlocked;
    setAttendanceUnlocked(newVal);
    try {
      const res = await fetch(`${API_URL}/api/attendance-lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unlocked: newVal }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch {
      setAttendanceUnlocked(!newVal);
      alert('Could not update the lock — check connection and try again.');
    }
  };

  useEffect(() => {
    fetch(`${API_URL}/api/attendance-lock`)
      .then(r => r.json())
      .then(d => { if (d.success) setAttendanceUnlocked(!!d.unlocked); })
      .catch(() => {});
  }, []);

  const clearAttendance = async () => {
    if (adminDates.length === 0) return;
    setShowConfirm(false);
    setAdminLoading(true); setAdminResult(null);
    try {
      const busParam = adminBus.trim() || null;
      let endpoint = `${API_URL}/api/bus-tracking/clear-attendance`;
      if (busParam) endpoint += `?bus_number=${encodeURIComponent(busParam)}`;
      const res = await fetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(adminDates)
      });
      const data = await res.json();
      if (res.ok) {
        setAdminResult({ success: true, message: `Cleared ${data.deleted} record(s) for ${data.bus_number}` });
        setAdminDates([]);
      } else {
        setAdminResult({ success: false, message: data.detail || 'Failed' });
      }
    } catch (err) {
      setAdminResult({ success: false, message: err.message });
    }
    setAdminLoading(false);
  };

  // ---- Planned Absences ----
  const loadAbsences = useCallback(async () => {
    setAbsencesLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/planned-absences`);
      const data = await res.json();
      if (data.success) setAbsencesList(data.absences || []);
    } catch { /* ignore */ }
    setAbsencesLoading(false);
  }, []);

  const loadAllCampers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/campers`);
      const data = await res.json();
      if (Array.isArray(data)) setAllCampers(data);
      else if (Array.isArray(data?.campers)) setAllCampers(data.campers);
    } catch { /* ignore */ }
  }, []);

  // Load absences + camper list — invoked from the tab onClick handler (no effect).
  const openAbsencesTab = useCallback(() => {
    setAdminTab('absences');
    loadAbsences();
    if (allCampers.length === 0) loadAllCampers();
  }, [loadAbsences, loadAllCampers, allCampers.length]);

  // ------- Message Broadcast (admin) -------
  const loadBroadcasts = useCallback(async () => {
    setBroadcastLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/broadcasts?admin_password=${encodeURIComponent(ADMIN_PASSWORD)}`);
      if (r.ok) {
        const j = await r.json();
        setBroadcastList(j.broadcasts || []);
      }
    } catch { /* ignore */ }
    finally { setBroadcastLoading(false); }
  }, []);

  const openBroadcastTab = useCallback(() => {
    setAdminTab('broadcast');
    setBroadcastFeedback(null);
    loadBroadcasts();
  }, [loadBroadcasts]);

  const sendBroadcast = useCallback(async () => {
    const msg = broadcastMessage.trim();
    if (!msg) { setBroadcastFeedback({ success: false, text: 'Message cannot be empty' }); return; }
    setBroadcastSending(true);
    setBroadcastFeedback(null);
    try {
      const r = await fetch(`${API_URL}/api/broadcasts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, urgent: broadcastUrgent, admin_password: ADMIN_PASSWORD }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed to send');
      setBroadcastMessage('');
      setBroadcastUrgent(false);
      setBroadcastFeedback({ success: true, text: 'Sent to all buses. Auto-expires in 4h.' });
      loadBroadcasts();
    } catch (e) {
      setBroadcastFeedback({ success: false, text: e.message || 'Failed to send' });
    } finally {
      setBroadcastSending(false);
    }
  }, [broadcastMessage, broadcastUrgent, loadBroadcasts]);

  const dismissBroadcast = useCallback(async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/broadcasts/${id}?admin_password=${encodeURIComponent(ADMIN_PASSWORD)}`, {
        method: 'DELETE',
      });
      if (r.ok) loadBroadcasts();
    } catch { /* ignore */ }
  }, [loadBroadcasts]);




  // Camper search (client-side filter)
  const absenceCamperResults = React.useMemo(() => {
    const q = absenceCamperQuery.trim().toLowerCase();
    if (!q) return [];
    return allCampers
      .filter(c => `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [absenceCamperQuery, allCampers]);

  const submitAbsence = async () => {
    setAbsenceMessage(null);
    if (!absenceSelectedCamper) { setAbsenceMessage({ success: false, text: 'Pick a camper' }); return; }
    if (!absenceStartDate || !absenceEndDate) { setAbsenceMessage({ success: false, text: 'Pick start and end dates' }); return; }
    if (absenceEndDate < absenceStartDate) { setAbsenceMessage({ success: false, text: 'End date must be on or after start' }); return; }
    try {
      const res = await fetch(`${API_URL}/api/planned-absences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          camper_id: absenceSelectedCamper.id || absenceSelectedCamper._id,
          start_date: absenceStartDate,
          end_date: absenceEndDate,
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setAbsenceMessage({ success: true, text: `Saved: ${absenceSelectedCamper.first_name} ${absenceSelectedCamper.last_name}` });
        setAbsenceSelectedCamper(null);
        setAbsenceCamperQuery('');
        setAbsenceStartDate('');
        setAbsenceEndDate('');
        loadAbsences();
      } else {
        setAbsenceMessage({ success: false, text: data.detail || 'Failed' });
      }
    } catch (err) {
      setAbsenceMessage({ success: false, text: err.message });
    }
  };

  const deleteAbsence = async (id) => {
    try {
      await fetch(`${API_URL}/api/planned-absences/${id}`, { method: 'DELETE' });
      loadAbsences();
    } catch { /* ignore */ }
  };

  const markAttendance = async (camperId, status) => {
    if (!busData) return;
    setAttendance(prev => ({ ...prev, [camperId]: status }));
    try {
      const res = await fetchWithRetry(
        `${API_URL}/api/bus-tracking/attendance?bus_number=${encodeURIComponent(busData.bus_number)}&date=${selectedDate}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ camper_id: camperId, status }),
        },
        'mark-attendance'
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch {
      // After 4 retries we still failed. Revert UI to unmarked so counselor sees it didn't save.
      setAttendance(prev => { const n = { ...prev }; delete n[camperId]; return n; });
    }
  };

  // NOTE: The UNDO feature was removed 2026-07-28 after RCA showed its
  // "unmarked" push is rejected by CamperSnapshot (400), stranding phantom
  // absents. Mistakes are corrected by re-marking (Present <-> Absent),
  // which always pushes successfully.

  // Login screen
  if (!isLoggedIn) {
    return (
      <div data-testid="counselor-login" style={{minHeight:'100vh',background:'linear-gradient(135deg,#2563eb,#1e40af)',display:'flex',alignItems:'center',justifyContent:'center',padding:16,position:'relative'}}>
        {showHelp && <CounselorHelp onClose={() => setShowHelp(false)} />}
        <button data-testid="help-btn-login" onClick={() => setShowHelp(true)}
          style={{position:'absolute',top:'max(env(safe-area-inset-top, 12px), 40px)',right:16,background:'rgba(255,255,255,0.2)',border:'none',color:'white',padding:'8px 14px',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:6}}>
          <HelpCircle size={16} /> Help
        </button>
        <div style={{background:'white',borderRadius:16,padding:32,width:'100%',maxWidth:320}}>
          <div style={{textAlign:'center',marginBottom:24}}>
            <div style={{width:80,height:80,background:'#dbeafe',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px'}}><Bus size={40} color="#2563eb" /></div>
            <h1 style={{fontSize:24,fontWeight:'bold',margin:0}}>Bus Tracker</h1>
            <p style={{color:'#6b7280',margin:'4px 0 0'}}>Enter your bus number</p>
          </div>
          <form onSubmit={handleLogin}>
            <input data-testid="bus-pin-input" type="text" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Bus # or admin" 
              style={{width:'100%',padding:16,fontSize:28,textAlign:'center',fontWeight:'bold',border:'2px solid #e5e7eb',borderRadius:12,boxSizing:'border-box'}} autoFocus />
            {error && <div data-testid="login-error" style={{marginTop:16,padding:12,background:'#fef2f2',borderRadius:8,color:'#dc2626',textAlign:'center'}}>{error}</div>}
            <button data-testid="login-submit-btn" type="submit" disabled={loading||!pin} style={{width:'100%',padding:16,marginTop:16,background:'#2563eb',color:'white',fontSize:18,fontWeight:600,border:'none',borderRadius:12,opacity:loading||!pin?0.5:1}}>{loading?'Loading...':'Go'}</button>
          </form>
          <p style={{textAlign:'center',fontSize:10,color:'#999',marginTop:16}}>{APP_VERSION}</p>
        </div>
      </div>
    );
  }

  // Admin panel
  if (isAdmin) {
    // Password gate
    if (!adminAuthenticated) {
      return (
        <div data-testid="admin-password-gate" style={{minHeight:'100vh',background:'#111827',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#1f2937',borderRadius:16,padding:32,width:'100%',maxWidth:320,border:'1px solid #374151'}}>
            <div style={{textAlign:'center',marginBottom:24}}>
              <div style={{width:64,height:64,background:'#dc2626',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px'}}>
                <Lock size={28} color="white" />
              </div>
              <h2 style={{color:'white',fontSize:20,fontWeight:700,margin:0}}>Admin Access</h2>
              <p style={{color:'#9ca3af',fontSize:13,margin:'4px 0 0'}}>Enter admin password</p>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (adminPasswordInput === ADMIN_PASSWORD) {
                setAdminAuthenticated(true);
                setAdminPasswordError('');
                // Persist admin session so a manual reset / refresh doesn't
                // kick the admin back to the PIN screen.
                try { localStorage.setItem('counselor_admin_mode', 'true'); } catch { /* quota */ }
              } else {
                setAdminPasswordError('Wrong password');
              }
            }}>
              <input
                data-testid="admin-password-input"
                type="password"
                value={adminPasswordInput}
                onChange={(e) => setAdminPasswordInput(e.target.value)}
                placeholder="Password"
                style={{width:'100%',padding:14,fontSize:16,textAlign:'center',border:'2px solid #374151',borderRadius:10,background:'#111827',color:'white',boxSizing:'border-box'}}
                autoFocus
              />
              {adminPasswordError && <div data-testid="admin-password-error" style={{marginTop:12,padding:8,background:'#450a0a',borderRadius:6,color:'#f87171',textAlign:'center',fontSize:13}}>{adminPasswordError}</div>}
              <button data-testid="admin-password-submit" type="submit" disabled={!adminPasswordInput}
                style={{width:'100%',padding:14,marginTop:16,background:'#dc2626',color:'white',fontSize:16,fontWeight:600,border:'none',borderRadius:10,cursor:'pointer',opacity:!adminPasswordInput?0.5:1}}>
                Enter
              </button>
            </form>
            <button data-testid="admin-password-back" onClick={handleLogout}
              style={{width:'100%',padding:10,marginTop:12,background:'none',border:'1px solid #374151',color:'#9ca3af',fontSize:13,borderRadius:8,cursor:'pointer'}}>
              Back to Login
            </button>
          </div>
        </div>
      );
    }

    const generateDates = () => {
      const dates = [];
      const start = new Date('2026-06-29T12:00:00');
      const end = new Date('2026-08-20T12:00:00');
      const excluded = ['2026-07-03'];
      const d = new Date(start);
      while (d <= end) {
        const day = d.getDay();
        const iso = d.toISOString().split('T')[0];
        if (day >= 1 && day <= 5 && !excluded.includes(iso)) {
          dates.push(iso);
        }
        d.setDate(d.getDate() + 1);
      }
      return dates;
    };
    const campDates = generateDates();
    const today = getTodayEST();

    return (
      <div data-testid="admin-panel" className="counselor-page" style={{minHeight:'100vh',background:'#111827',paddingBottom:20}}>
        <div style={{position:'sticky',top:0,zIndex:100,background:'#dc2626',color:'white',padding:'12px',paddingTop:'max(env(safe-area-inset-top, 12px), 40px)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <Shield size={20} />
              <h1 style={{margin:0,fontSize:18,fontWeight:'bold'}}>Admin</h1>
            </div>
            <button data-testid="admin-logout-btn" onClick={handleLogout} style={{background:'none',border:'none',color:'white',padding:8,cursor:'pointer'}}><LogOut size={20} /></button>
          </div>
          {/* Attendance unlock toggle in header */}
          <button data-testid="admin-toggle-lock-btn" onClick={toggleAttendanceLock}
            style={{
              width:'100%',marginTop:10,padding:'10px 14px',borderRadius:8,border:'none',cursor:'pointer',
              display:'flex',alignItems:'center',justifyContent:'space-between',
              background:attendanceUnlocked?'#422006':'rgba(0,0,0,0.3)'
            }}>
            <span style={{fontSize:13,fontWeight:600,color:'white'}}>
              {attendanceUnlocked ? 'Attendance: UNLOCKED' : 'Attendance: LOCKED (9:30 AM)'}
            </span>
            <span style={{
              padding:'4px 12px',borderRadius:6,fontSize:12,fontWeight:700,
              background:attendanceUnlocked?'#dc2626':'#f59e0b',
              color:attendanceUnlocked?'white':'#000'
            }}>
              {attendanceUnlocked ? 'Re-Lock' : 'Unlock'}
            </span>
          </button>
          {/* Tabs */}
          <div style={{display:'flex',gap:4,marginTop:10,flexWrap:'wrap'}}>
            <button data-testid="admin-tab-live" onClick={() => setAdminTab('live')}
              style={{flex:1,minWidth:90,padding:'8px 0',borderRadius:6,border:'none',fontSize:13,fontWeight:600,cursor:'pointer',
                background:adminTab==='live'?'white':'rgba(255,255,255,0.15)',
                color:adminTab==='live'?'#dc2626':'white'}}>
              <Activity size={14} style={{marginRight:4,verticalAlign:'middle'}} /> Live
            </button>
            <button data-testid="admin-tab-routes" onClick={() => setAdminTab('routes')}
              style={{flex:1,minWidth:90,padding:'8px 0',borderRadius:6,border:'none',fontSize:13,fontWeight:600,cursor:'pointer',
                background:adminTab==='routes'?'white':'rgba(255,255,255,0.15)',
                color:adminTab==='routes'?'#dc2626':'white'}}>
              <Map size={14} style={{marginRight:4,verticalAlign:'middle'}} /> Routes
            </button>
            <button data-testid="admin-tab-audit" onClick={() => setAdminTab('audit')}
              style={{flex:1,minWidth:90,padding:'8px 0',borderRadius:6,border:'none',fontSize:13,fontWeight:600,cursor:'pointer',
                background:adminTab==='audit'?'white':'rgba(255,255,255,0.15)',
                color:adminTab==='audit'?'#dc2626':'white'}}>
              <ClipboardList size={14} style={{marginRight:4,verticalAlign:'middle'}} /> Audit Log
            </button>
            <button data-testid="admin-tab-clear" onClick={() => setAdminTab('clear')}
              style={{flex:1,minWidth:90,padding:'8px 0',borderRadius:6,border:'none',fontSize:13,fontWeight:600,cursor:'pointer',
                background:adminTab==='clear'?'white':'rgba(255,255,255,0.15)',
                color:adminTab==='clear'?'#dc2626':'white'}}>
              <Trash2 size={14} style={{marginRight:4,verticalAlign:'middle'}} /> Clear Data
            </button>
            <button data-testid="admin-tab-absences" onClick={openAbsencesTab}
              style={{flex:1,minWidth:120,padding:'8px 0',borderRadius:6,border:'none',fontSize:13,fontWeight:600,cursor:'pointer',
                background:adminTab==='absences'?'white':'rgba(255,255,255,0.15)',
                color:adminTab==='absences'?'#dc2626':'white'}}>
              <CalendarOff size={14} style={{marginRight:4,verticalAlign:'middle'}} /> Planned Absences
            </button>
            <button data-testid="admin-tab-broadcast" onClick={openBroadcastTab}
              style={{flex:1,minWidth:120,padding:'8px 0',borderRadius:6,border:'none',fontSize:13,fontWeight:600,cursor:'pointer',
                background:adminTab==='broadcast'?'white':'rgba(255,255,255,0.15)',
                color:adminTab==='broadcast'?'#dc2626':'white'}}>
              <Megaphone size={14} style={{marginRight:4,verticalAlign:'middle'}} /> Message Broadcast
            </button>
          </div>
        </div>

        <div style={{padding:12}}>
          {adminTab === 'live' ? (
            <LiveAttendanceDashboard />
          ) : adminTab === 'audit' ? (
            <AttendanceAuditLog />
          ) : adminTab === 'routes' ? (
            <AdminRouteHistory />
          ) : adminTab === 'absences' ? (
            <PlannedAbsencesPanel
              absencesList={absencesList}
              absencesLoading={absencesLoading}
              query={absenceCamperQuery}
              setQuery={setAbsenceCamperQuery}
              results={absenceCamperResults}
              selected={absenceSelectedCamper}
              setSelected={setAbsenceSelectedCamper}
              startDate={absenceStartDate}
              setStartDate={setAbsenceStartDate}
              endDate={absenceEndDate}
              setEndDate={setAbsenceEndDate}
              message={absenceMessage}
              onSubmit={submitAbsence}
              onDelete={deleteAbsence}
              today={today}
            />
          ) : adminTab === 'broadcast' ? (
            <BroadcastPanel
              message={broadcastMessage}
              setMessage={setBroadcastMessage}
              urgent={broadcastUrgent}
              setUrgent={setBroadcastUrgent}
              list={broadcastList}
              loading={broadcastLoading}
              sending={broadcastSending}
              feedback={broadcastFeedback}
              onSend={sendBroadcast}
              onDismiss={dismissBroadcast}
            />
          ) : (
            <>

              {/* Bus filter */}
              <div style={{marginBottom:12}}>
                <label style={{color:'#9ca3af',fontSize:12,fontWeight:600,display:'block',marginBottom:4}}>BUS (leave empty for ALL buses)</label>
                <input
                  data-testid="admin-bus-input"
                  type="text"
                  value={adminBus}
                  onChange={(e) => setAdminBus(e.target.value)}
                  placeholder="e.g. Bus #14"
                  style={{width:'100%',padding:10,background:'#1f2937',border:'1px solid #374151',borderRadius:8,color:'white',fontSize:14,boxSizing:'border-box'}}
                />
              </div>

              {/* Quick select */}
              <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                <button data-testid="admin-select-today" onClick={() => setAdminDates([today])}
                  style={{padding:'6px 12px',background:'#374151',border:'none',borderRadius:6,color:'white',fontSize:12,cursor:'pointer'}}>Today</button>
                <button data-testid="admin-select-all" onClick={() => setAdminDates([...campDates])}
                  style={{padding:'6px 12px',background:'#374151',border:'none',borderRadius:6,color:'white',fontSize:12,cursor:'pointer'}}>All Camp Days</button>
                <button data-testid="admin-select-none" onClick={() => setAdminDates([])}
                  style={{padding:'6px 12px',background:'#374151',border:'none',borderRadius:6,color:'white',fontSize:12,cursor:'pointer'}}>Clear Selection</button>
              </div>

              {/* Custom date */}
              <div style={{marginBottom:12,display:'flex',gap:8}}>
                <input
                  data-testid="admin-custom-date"
                  type="date"
                  style={{flex:1,padding:8,background:'#1f2937',border:'1px solid #374151',borderRadius:8,color:'white',fontSize:13,colorScheme:'dark'}}
                  onChange={(e) => { if (e.target.value && !adminDates.includes(e.target.value)) setAdminDates(prev => [...prev, e.target.value]); }}
                />
              </div>

              {/* Date grid */}
              <label style={{color:'#9ca3af',fontSize:12,fontWeight:600,display:'block',marginBottom:8}}>
                CAMP DAYS ({adminDates.length} selected)
              </label>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(105px, 1fr))',gap:6,marginBottom:16}}>
                {campDates.map(date => {
                  const selected = adminDates.includes(date);
                  const d = new Date(date + 'T12:00:00');
                  const label = d.toLocaleDateString('en-US', {weekday:'short', month:'short', day:'numeric'});
                  return (
                    <button key={date} data-testid={`admin-date-${date}`} onClick={() => toggleAdminDate(date)}
                      style={{
                        padding:'8px 4px',fontSize:11,fontWeight:selected?700:400,
                        background:selected?'#dc2626':'#1f2937',
                        color:selected?'white':'#9ca3af',
                        border:selected?'2px solid #f87171':'1px solid #374151',
                        borderRadius:8,cursor:'pointer',textAlign:'center'
                      }}>
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* Result message */}
              {adminResult && (
                <div data-testid="admin-result" style={{
                  marginBottom:12,padding:12,borderRadius:8,
                  background:adminResult.success?'#052e16':'#450a0a',
                  color:adminResult.success?'#4ade80':'#f87171',
                  fontSize:14,fontWeight:500
                }}>
                  {adminResult.message}
                </div>
              )}

              {/* Confirmation dialog */}
              {showConfirm && (
                <div data-testid="admin-confirm-dialog" style={{marginBottom:12,padding:16,background:'#450a0a',border:'2px solid #dc2626',borderRadius:12}}>
                  <p style={{color:'#fca5a5',fontSize:14,fontWeight:600,margin:'0 0 8px'}}>
                    Clear attendance for {adminBus.trim() || 'ALL buses'} on {adminDates.length} date(s)?
                  </p>
                  <p style={{color:'#9ca3af',fontSize:12,margin:'0 0 12px'}}>This cannot be undone.</p>
                  <div style={{display:'flex',gap:8}}>
                    <button data-testid="admin-confirm-yes" onClick={clearAttendance}
                      style={{flex:1,padding:12,background:'#dc2626',color:'white',fontWeight:700,border:'none',borderRadius:8,cursor:'pointer',fontSize:14}}>
                      Yes, Clear
                    </button>
                    <button data-testid="admin-confirm-no" onClick={() => setShowConfirm(false)}
                      style={{flex:1,padding:12,background:'#374151',color:'white',fontWeight:600,border:'none',borderRadius:8,cursor:'pointer',fontSize:14}}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Clear button */}
              <button
                data-testid="admin-clear-btn"
                onClick={() => setShowConfirm(true)}
                disabled={adminDates.length === 0 || adminLoading}
                style={{
                  width:'100%',padding:16,background:adminDates.length === 0?'#374151':'#dc2626',
                  color:'white',fontSize:16,fontWeight:700,border:'none',borderRadius:12,
                  cursor:adminDates.length===0?'not-allowed':'pointer',
                  opacity:adminLoading?0.5:1,
                  display:'flex',alignItems:'center',justifyContent:'center',gap:8
                }}>
                <Trash2 size={20} />
                {adminLoading ? 'Clearing...' : `Clear ${adminDates.length} Date(s)`}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const baseCampers = busData?.campers || [];

  // Merge live roster data into campers
  // ----- ROSTER CONSTRUCTION (CamperSnapshot is the source of truth) -----
  //
  // The displayed bus roster comes DIRECTLY from CamperSnapshot's daily roster
  // (liveRoster.campers). CamperSnapshot already accounts for flex days, session
  // dates, and bus assignments — we just render what it returns.
  //
  // Local DB (baseCampers) is used ONLY for:
  //   1. Enriching each CamperSnapshot camper with their local `id` (needed
  //      for marking attendance) and `address`. Match by snapshot_id first,
  //      then by name.
  //   2. Fallback when CamperSnapshot is unreachable, so the bus is never
  //      silently blank.
  //
  // This means: change in CamperSnapshot → reflected immediately. Change in
  // local DB without a push to CamperSnapshot → NOT reflected. That's the
  // explicit policy: CamperSnapshot is the daily-roster source of truth.
  const hasLiveRoster = liveRoster?.success === true;

  // Local-DB lookup maps (snapshot_id → camper, lowercased "first last" → camper).
  const localById = {};
  const localByName = {};
  for (const b of baseCampers) {
    if (b.snapshot_id) localById[b.snapshot_id] = b;
    const nk = `${b.first_name || ''} ${b.last_name || ''}`.trim().toLowerCase();
    if (nk) localByName[nk] = b;
  }

  let campers;
  if (hasLiveRoster) {
    // CamperSnapshot returned a roster — use it verbatim, enriched with local data.
    campers = (liveRoster.campers || []).map(live => {
      const nameKey = (live.name || '').toLowerCase().trim();
      const local = (live.snapshot_id && localById[live.snapshot_id]) || localByName[nameKey] || {};
      const parts = (live.name || '').trim().split(/\s+/);
      const firstName = local.first_name || parts[0] || '';
      const lastName = local.last_name || parts.slice(1).join(' ') || '';
      // CamperSnapshot returns `sessions` as an array of objects with shape
      // {session_id, name, start, end, is_flex}. Defensively also accept a
      // plain-string sessions payload or a single-object sessions payload.
      // Extract the human-readable name; never push the object itself into
      // state — React can't render it (error #31).
      const rawSession = Array.isArray(live.sessions) ? live.sessions[0] : live.sessions;
      const sessionStr =
        (rawSession && typeof rawSession === 'object' && rawSession.name) ||
        (typeof rawSession === 'string' ? rawSession : '') ||
        local.session ||
        '';
      return {
        // Local id is needed for marking attendance. Fall back to a stable
        // CamperSnapshot-derived id when no local match exists.
        id: local.id || `snap_${live.snapshot_id || nameKey}`,
        first_name: firstName,
        last_name: lastName,
        address: local.address || '',
        am_bus: local.am_bus || '',
        pm_bus: local.pm_bus || '',
        session: sessionStr,
        snapshot_id: live.snapshot_id || local.snapshot_id || '',
        rides_am: live.rides_am ?? true,
        rides_pm: live.rides_pm ?? true,
        am_excepted_today: live.am_excepted_today ?? false,
        am_exception_location: live.am_exception_location ?? null,
        pm_excepted_today: live.pm_excepted_today ?? false,
        pm_exception_location: live.pm_exception_location ?? null,
        early_swim_lesson: live.early_swim_lesson ?? false,
        todays_swim_lesson: live.todays_swim_lesson ?? '',
        group: live.group || '',
        age: live.age,
        late_arrival_today: live.late_arrival_today ?? false,
        early_dismissed_today: live.early_dismissed_today ?? false,
        dismissal_time: live.dismissal_time,
        pickup_by: live.pickup_by,
        is_flex: live.is_flex ?? false,
        session_changeover: live.session_changeover,
        attendance: live.attendance,
      };
    });
  } else {
    // Fallback: CamperSnapshot is unreachable. Show the full local-DB roster
    // so the bus isn't silently blank during an outage.
    campers = baseCampers.map(c => ({
      ...c,
      rides_am: c.rides_am ?? true,
      rides_pm: c.rides_pm ?? true,
      group: c.group || '',
    }));
  }

  // removed_today comes directly from CamperSnapshot — these kids are NOT on the bus this period.
  // Dedupe by name (safety net): the same kid can arrive via two removal
  // pipelines (absence + exception). Prefer the 'Absent' entry when both exist.
  const removedToday = (() => {
    const byName = {};
    const extras = [];
    for (const r of (liveRoster?.removed_today || [])) {
      const key = (r.name || '').trim().toLowerCase();
      if (!key) { extras.push(r); continue; }
      const existing = byName[key];
      const isAbsent = (x) => x.reason_type === 'absent' || (x.reason || '').toLowerCase().includes('absent');
      if (!existing || (isAbsent(r) && !isAbsent(existing))) byName[key] = r;
    }
    return [...Object.values(byName), ...extras];
  })();
  const removedNames = new Set(removedToday.map(r => r.name?.toLowerCase()));

  const swimAm = liveRoster?.swim_am || busData?.swim_am || [];
  const swimPm = liveRoster?.swim_pm || busData?.swim_pm || [];

  // Remember every camper we've seen on this bus/period so an accidental
  // Absent tap doesn't erase them from the counselor's screen. When
  // CamperSnapshot later removes the kid from the daily roster in response
  // to our own push, we re-inject them from this sticky ref so the
  // counselor can still tap Present to correct the mistake.
  for (const c of campers) {
    if (c && c.id) seenCampersRef.current[c.id] = c;
  }

  const activeRiders = (() => {
    const list = campers.filter(c => {
      const name = `${c.first_name} ${c.last_name}`.toLowerCase();
      // Period scoping (universal): PM-only kids never appear on AM, AM-only
      // kids never appear on PM. CamperSnapshot sets these flags per-camper
      // per-period. The fallback path (no live roster) uses local flags.
      if (activePeriod === 'am' && c.rides_am === false) return false;
      if (activePeriod === 'pm' && c.rides_pm === false) return false;
      return !removedNames.has(name);
    });

    // Re-inject any locally-marked camper that CamperSnapshot dropped from
    // the roster — the counselor still needs to be able to tap Present or
    // re-mark Present to correct an accidental Absent.
    const presentIds = new Set(list.map(c => c.id));
    for (const camperId of Object.keys(attendance)) {
      if (presentIds.has(camperId)) continue;
      const cached = seenCampersRef.current[camperId];
      if (!cached) continue;
      // Respect period scoping — don't reveal an AM-only kid on the PM tab.
      if (activePeriod === 'am' && cached.rides_am === false) continue;
      if (activePeriod === 'pm' && cached.rides_pm === false) continue;
      list.push({ ...cached, _restored: true });
    }
    return list;
  })();

  // Counts scoped to the visible roster so Unmarked can never go negative
  // (an absent kid dropped from the roster no longer skews the math).
  const presentCount = activeRiders.filter(c => attendance[c.id] === 'present').length;
  const absentCount = activeRiders.filter(c => attendance[c.id] === 'absent').length;



  return (
    <div data-testid="counselor-main" className="counselor-page" style={{
      minHeight:'100vh',
      background:'#f3f4f6',
      paddingBottom:20
    }}>
      {showHelp && <CounselorHelp onClose={() => setShowHelp(false)} />}
      {/* Sticky Header */}
      <div data-testid="counselor-header" style={{
        position: 'sticky', 
        top: 0, 
        zIndex: 100, 
        background: '#2563eb', 
        color: 'white', 
        padding: '12px 12px 12px 12px',
        paddingTop: 'max(env(safe-area-inset-top, 12px), 40px)'
      }}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <h1 data-testid="bus-number-title" style={{margin:0,fontSize:18,fontWeight:'bold'}}>{busData?.bus_number}</h1>
          <div style={{display:'flex',alignItems:'center',gap:4}}>
            <button data-testid="help-btn" onClick={() => setShowHelp(true)} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'white',padding:'6px 10px',borderRadius:6,fontSize:12,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
              <HelpCircle size={14} /> Help
            </button>
            <button data-testid="logout-btn" onClick={handleLogout} style={{background:'none',border:'none',color:'white',padding:8,cursor:'pointer'}}><LogOut size={20} /></button>
          </div>
        </div>
        <div style={{marginTop:8,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <span data-testid="gps-status" style={{display:'inline-flex',alignItems:'center',gap:4,padding:'4px 10px',borderRadius:20,fontSize:12,fontWeight:500,
            background:gpsStatus==='tracking'?'#22c55e':gpsStatus==='error'?'#ef4444':'#eab308'}}>
            <Navigation size={12} />
            {gpsStatus==='tracking'?`GPS (${locationCount})`:gpsStatus==='error'?'GPS Off':'Starting...'}
          </span>
          {wakeLockActive && <span style={{fontSize:10,background:'rgba(255,255,255,0.2)',padding:'4px 8px',borderRadius:20}}>🔒</span>}
          {gpsStatus==='error' && <button data-testid="gps-retry-btn" onClick={startGpsTracking} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'white',padding:'4px 10px',borderRadius:20,fontSize:12,cursor:'pointer'}}>Retry</button>}
        </div>
        {gpsMessage && <p data-testid="gps-message" style={{margin:'4px 0 0',fontSize:11,color:'#bfdbfe'}}>{gpsMessage}</p>}
        {/* Date Selector */}
        <div data-testid="date-selector" style={{marginTop:8,display:'flex',alignItems:'center',gap:8}}>
          <Calendar size={14} />
          <input
            data-testid="date-input"
            type="date"
            value={selectedDate}
            onChange={(e) => handleDateChange(e.target.value)}
            style={{
              background:'rgba(255,255,255,0.15)',
              border:'1px solid rgba(255,255,255,0.3)',
              borderRadius:8,
              color:'white',
              padding:'4px 8px',
              fontSize:13,
              fontWeight:500,
              flex:1,
              colorScheme:'dark'
            }}
          />
          {selectedDate !== getTodayEST() && (
            <button
              data-testid="date-today-btn"
              onClick={() => handleDateChange(getTodayEST())}
              style={{background:'rgba(255,255,255,0.2)',border:'none',color:'white',padding:'4px 10px',borderRadius:8,fontSize:11,cursor:'pointer',whiteSpace:'nowrap'}}
            >
              Today
            </button>
          )}
        </div>
        {selectedDate !== getTodayEST() && (
          <div style={{marginTop:4,padding:'3px 8px',background:'#f59e0b',borderRadius:6,fontSize:11,fontWeight:600,color:'#000',textAlign:'center'}}>
            Testing: {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {weekday:'short', month:'short', day:'numeric', year:'numeric'})}
          </div>
        )}
        {/* AM / PM Toggle — always visible in sticky header */}
        <div style={{marginTop:8,display:'flex',alignItems:'center',justifyContent:'flex-start'}}>
          <div style={{display:'flex',gap:0,borderRadius:8,overflow:'hidden',border:'2px solid rgba(255,255,255,0.3)'}}>
            <button data-testid="period-am-btn" onClick={() => { setActivePeriod('am'); setLiveRoster(null); }}
              style={{padding:'8px 20px',border:'none',fontSize:14,fontWeight:700,cursor:'pointer',
                background:activePeriod==='am'?'white':'transparent',
                color:activePeriod==='am'?'#2563eb':'rgba(255,255,255,0.7)'}}>
              AM Pickup
            </button>
            <button data-testid="period-pm-btn" onClick={() => { setActivePeriod('pm'); setLiveRoster(null); }}
              style={{padding:'8px 20px',border:'none',fontSize:14,fontWeight:700,cursor:'pointer',
                background:activePeriod==='pm'?'white':'transparent',
                color:activePeriod==='pm'?'#7c3aed':'rgba(255,255,255,0.7)'}}>
              PM Dropoff
            </button>
          </div>
        </div>
      </div>

      {/* Broadcast Banner — visible to counselors + admins whenever an active broadcast exists */}
      {visibleBroadcasts.length > 0 && (
        <div data-testid="broadcast-banner-container">
          {visibleBroadcasts.map(b => (
            <div key={b.id} data-testid={`broadcast-banner-${b.id}`}
              style={{
                background: b.urgent ? '#dc2626' : '#059669',
                color: 'white',
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 10,
                borderBottom: '1px solid rgba(0,0,0,0.2)'
              }}>
              <div style={{display:'flex',alignItems:'flex-start',gap:8,flex:1,minWidth:0}}>
                <Megaphone size={18} style={{flexShrink:0,marginTop:1}} />
                <div style={{minWidth:0}}>
                  {b.urgent && <div style={{fontSize:10,fontWeight:800,letterSpacing:0.5,marginBottom:2}}>URGENT</div>}
                  <div style={{fontSize:14,fontWeight:600,lineHeight:1.35,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{b.message}</div>
                </div>
              </div>
              <button
                data-testid={`broadcast-ack-${b.id}`}
                onClick={() => dismissBroadcastLocally(b.id)}
                style={{
                  background:'rgba(255,255,255,0.25)',border:'none',color:'white',
                  padding:'6px 12px',borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',flexShrink:0
                }}
              >
                Got it
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Attendance Closed Banner */}
      {attendanceClosed && !attendanceUnlocked && (
        <div data-testid="attendance-closed-banner" style={{background:'#dc2626',color:'white',padding:'8px 12px',textAlign:'center',fontSize:13,fontWeight:600}}>
          Attendance closed at 9:30 AM
        </div>
      )}
      {attendanceClosed && attendanceUnlocked && (
        <div data-testid="attendance-unlocked-banner" style={{background:'#f59e0b',color:'#000',padding:'8px 12px',textAlign:'center',fontSize:13,fontWeight:600}}>
          Attendance unlocked by admin
        </div>
      )}

      {/* Stats Bar */}
      <div data-testid="stats-bar" style={{background:'white',borderBottom:'1px solid #e5e7eb',padding:8,display:'flex',justifyContent:'space-around'}}>
        <div style={{textAlign:'center'}}><div data-testid="present-count" style={{fontSize:20,fontWeight:'bold',color:'#22c55e'}}>{presentCount}</div><div style={{fontSize:10,color:'#6b7280'}}>Present</div></div>
        <div style={{textAlign:'center'}}><div data-testid="absent-count" style={{fontSize:20,fontWeight:'bold',color:'#ef4444'}}>{absentCount}</div><div style={{fontSize:10,color:'#6b7280'}}>Absent</div></div>
        <div style={{textAlign:'center'}}><div data-testid="unmarked-count" style={{fontSize:20,fontWeight:'bold',color:'#9ca3af'}}>{activeRiders.length-presentCount-absentCount}</div><div style={{fontSize:10,color:'#6b7280'}}>Unmarked</div></div>
        <div style={{textAlign:'center'}}><div data-testid="total-count" style={{fontSize:20,fontWeight:'bold',color:'#2563eb'}}>{activeRiders.length}</div><div style={{fontSize:10,color:'#6b7280'}}>Total</div></div>
      </div>

      {/* Birthdays today — surfaced from CamperSnapshot /api/birthdays/today */}
      {(() => {
        const allBirthdays = liveRoster?.birthdays_today || [];
        // Only show kids who are riding THIS bus today (active in current period roster).
        const activeNames = new Set(activeRiders.map(c => `${c.first_name} ${c.last_name}`.toLowerCase()));
        const onBus = allBirthdays.filter(b => {
          const full = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase().trim();
          const pref = `${b.preferred_name || ''} ${b.last_name || ''}`.toLowerCase().trim();
          return activeNames.has(full) || activeNames.has(pref);
        });
        if (onBus.length === 0) return null;
        return (
          <div data-testid="birthdays-banner" style={{margin:8,marginBottom:0,background:'linear-gradient(135deg,#fef3c7 0%,#fce7f3 100%)',border:'2px solid #f59e0b',borderRadius:10,padding:12}}>
            <div style={{fontSize:13,fontWeight:700,color:'#92400e',marginBottom:6,display:'flex',alignItems:'center',gap:6}}>
              <span style={{fontSize:18}}>🎂</span>
              HAPPY BIRTHDAY {onBus.length > 1 ? `(${onBus.length})` : ''}
            </div>
            {onBus.map((b, i) => (
              <div key={b.id || i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'4px 0',borderBottom:i<onBus.length-1?'1px solid #fde68a':'none'}} data-testid={`birthday-row-${i}`}>
                <span style={{fontSize:14,fontWeight:600,color:'#7c2d12'}}>{b.preferred_name || b.first_name} {b.last_name}</span>
                <div style={{display:'flex',gap:6,alignItems:'center'}}>
                  {b.group_code && <span style={{fontSize:10,fontWeight:600,color:'#92400e',background:'#fef3c7',padding:'2px 6px',borderRadius:4}}>{b.group_code}</span>}
                  {b.turning_age && <span style={{fontSize:11,fontWeight:700,color:'white',background:'#f59e0b',padding:'2px 8px',borderRadius:12}}>Turns {b.turning_age}</span>}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Removed today — from CamperSnapshot removed_today array */}
      {removedToday.length > 0 && (
        <div data-testid="removed-today-section" style={{margin:8,marginBottom:0,background:'#fef2f2',border:'2px solid #fca5a5',borderRadius:10,padding:10}}>
          <div style={{fontSize:12,fontWeight:700,color:'#dc2626',marginBottom:6}}>
            NOT ON {activePeriod.toUpperCase()} BUS TODAY ({removedToday.length})
          </div>
          {removedToday.map((c, i) => (
            <div key={c.id || i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'4px 0',borderBottom:i<removedToday.length-1?'1px solid #fecaca':'none'}}>
              <span style={{fontSize:13,fontWeight:500,color:'#991b1b'}}>{c.name}</span>
              <span style={{fontSize:10,fontWeight:600,color:'white',padding:'2px 6px',borderRadius:4,background:'#dc2626'}}>
                {c.reason || 'Removed'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* AM Swim Lessons — only on AM tab */}
      {activePeriod === 'am' && swimAm.length > 0 && (
        <div data-testid="swim-am-section" style={{margin:8,marginBottom:0,background:'#ecfeff',border:'2px solid #06b6d4',borderRadius:10,padding:10}}>
          <div style={{fontSize:12,fontWeight:700,color:'#0891b2',marginBottom:6}}>AM SWIM LESSONS — NOT ON BUS</div>
          {swimAm.map((s, i) => (
            <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'4px 0',borderBottom:i<swimAm.length-1?'1px solid #cffafe':'none'}}>
              <span style={{fontSize:13,fontWeight:500,color:'#164e63'}}>{s.name}</span>
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                {s.group && <span style={{fontSize:10,fontWeight:600,color:'#0891b2',background:'#cffafe',padding:'1px 5px',borderRadius:3}}>{s.group}</span>}
                <span style={{fontSize:11,color:'#0e7490',fontWeight:600}}>{s.time}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PM Swim Lessons — only on PM tab */}
      {activePeriod === 'pm' && swimPm.length > 0 && (
        <div data-testid="swim-pm-section" style={{margin:8,marginBottom:0,background:'#fef3c7',border:'2px solid #f59e0b',borderRadius:10,padding:10}}>
          <div style={{fontSize:12,fontWeight:700,color:'#92400e',marginBottom:6}}>PM SWIM LESSONS — NOT ON PM BUS</div>
          {swimPm.map((s, i) => (
            <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'4px 0',borderBottom:i<swimPm.length-1?'1px solid #fde68a':'none'}}>
              <span style={{fontSize:13,fontWeight:500,color:'#78350f'}}>{s.name}</span>
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                {s.group && <span style={{fontSize:10,fontWeight:600,color:'#92400e',background:'#fde68a',padding:'1px 5px',borderRadius:3}}>{s.group}</span>}
                <span style={{fontSize:11,color:'#92400e',fontWeight:600}}>{s.time}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Camper List — active riders only */}
      <div data-testid="camper-list" style={{padding:8}}>
        {activeRiders.map((camper, index) => {
          const status = attendance[camper.id];
          return (
            <div key={camper.id} data-testid={`camper-row-${index}`} style={{
              background:status==='present'?'#f0fdf4':status==='absent'?'#fef2f2':'white',
              border:`2px solid ${status==='present'?'#86efac':status==='absent'?'#fca5a5':'#e5e7eb'}`,
              borderRadius:10,padding:10,marginBottom:8,display:'flex',alignItems:'center',justifyContent:'space-between'
            }}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <span style={{color:'#9ca3af',fontSize:12,width:24}}>#{index+1}</span>
                  <span data-testid={`camper-name-${index}`} style={{fontWeight:500,fontSize:14,color:'#1f2937',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{camper.first_name} {camper.last_name}</span>
                </div>
                <div style={{display:'flex',gap:5,marginTop:3,marginLeft:30,flexWrap:'wrap',alignItems:'center'}}>
                  {camper.group && <span style={{fontSize:11,fontWeight:600,color:'#2563eb',background:'#dbeafe',padding:'1px 6px',borderRadius:4}}>{camper.group}</span>}
                  {camper.age != null && <span style={{fontSize:11,color:'#6b7280'}}>age {Math.floor(camper.age)}</span>}
                  {camper.session && <span style={{fontSize:11,color:'#6b7280'}}>{camper.session}</span>}
                  {camper.is_flex && <span style={{fontSize:10,fontWeight:600,color:'#7c3aed',background:'#ede9fe',padding:'1px 5px',borderRadius:4}}>Flex</span>}
                </div>
              </div>
              <div style={{display:'flex',gap:6,flexShrink:0}}>
                {(() => { const locked = attendanceClosed && !attendanceUnlocked; return (<>
                <button data-testid={`mark-present-${index}`} onClick={()=>!locked && markAttendance(camper.id,'present')} disabled={locked} style={{width:44,height:44,borderRadius:10,border:'none',display:'flex',alignItems:'center',justifyContent:'center',cursor:locked?'not-allowed':'pointer',
                  background:status==='present'?'#22c55e':'#f3f4f6',color:status==='present'?'white':'#9ca3af',opacity:locked?0.4:1}}>
                  <CheckCircle size={24} />
                </button>
                <button data-testid={`mark-absent-${index}`} onClick={()=>!locked && markAttendance(camper.id,'absent')} disabled={locked} style={{width:44,height:44,borderRadius:10,border:'none',display:'flex',alignItems:'center',justifyContent:'center',cursor:locked?'not-allowed':'pointer',
                  background:status==='absent'?'#ef4444':'#f3f4f6',color:status==='absent'?'white':'#9ca3af',opacity:locked?0.4:1}}>
                  <XCircle size={24} />
                </button>
                </>); })()}
              </div>
            </div>
          );
        })}
        {activeRiders.length===0 && (
          <div style={{textAlign:'center',padding:48,color:'#6b7280'}}>
            <Users size={48} style={{opacity:0.5,marginBottom:12}} />
            <p>No campers on this bus</p>
          </div>
        )}
        <p style={{textAlign:'center',fontSize:10,color:'#999',padding:16}}>{APP_VERSION}</p>
      </div>
    </div>
  );
}


function BroadcastPanel({ message, setMessage, urgent, setUrgent, list, loading, sending, feedback, onSend, onDismiss }) {
  const fmtTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      });
    } catch { return iso; }
  };
  const active = list.filter(b => b.active);
  const past = list.filter(b => !b.active);
  return (
    <div data-testid="broadcast-panel">
      <p style={{color:'#9ca3af',fontSize:12,margin:'0 0 12px',lineHeight:1.4}}>
        Send a message to every logged-in bus counselor. It appears as a banner at the top of their screen until they tap &quot;Got it&quot; or 4 hours pass.
      </p>

      <label style={{color:'#9ca3af',fontSize:12,fontWeight:600,display:'block',marginBottom:6}}>MESSAGE</label>
      <textarea
        data-testid="broadcast-message-input"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        maxLength={500}
        placeholder="e.g. All buses stop at Coles Ave today &mdash; construction on Ridgewood Rd."
        style={{
          width:'100%',padding:10,background:'#1f2937',border:'1px solid #374151',
          borderRadius:8,color:'white',fontSize:14,boxSizing:'border-box',
          fontFamily:'inherit',resize:'vertical'
        }}
      />
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',margin:'6px 0 12px'}}>
        <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',color:'#f3f4f6',fontSize:13,fontWeight:600}}>
          <input
            data-testid="broadcast-urgent-checkbox"
            type="checkbox"
            checked={urgent}
            onChange={(e) => setUrgent(e.target.checked)}
            style={{width:16,height:16,accentColor:'#dc2626'}}
          />
          Mark URGENT (red banner)
        </label>
        <span style={{color:'#6b7280',fontSize:11}}>{message.length}/500</span>
      </div>

      <button
        data-testid="broadcast-send-btn"
        onClick={onSend}
        disabled={sending || !message.trim()}
        style={{
          width:'100%',padding:'12px 0',borderRadius:8,border:'none',
          background: sending || !message.trim() ? '#4b5563' : (urgent ? '#dc2626' : '#059669'),
          color:'white',fontSize:15,fontWeight:700,cursor: sending || !message.trim() ? 'not-allowed' : 'pointer',
          display:'flex',alignItems:'center',justifyContent:'center',gap:8
        }}
      >
        <Megaphone size={16} />
        {sending ? 'Sending…' : `Send${urgent ? ' URGENT' : ''} to All Buses`}
      </button>

      {feedback && (
        <div data-testid="broadcast-feedback" style={{
          marginTop:10,padding:10,borderRadius:8,fontSize:13,fontWeight:600,
          background: feedback.success ? '#064e3b' : '#7f1d1d',
          color: feedback.success ? '#a7f3d0' : '#fecaca',
          border:`1px solid ${feedback.success ? '#059669' : '#dc2626'}`
        }}>
          {feedback.text}
        </div>
      )}

      <div style={{marginTop:20,borderTop:'1px solid #374151',paddingTop:14}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
          <label style={{color:'#9ca3af',fontSize:12,fontWeight:600}}>ACTIVE BROADCASTS ({active.length})</label>
          {loading && <span style={{color:'#6b7280',fontSize:11}}>Loading…</span>}
        </div>
        {active.length === 0 && !loading && (
          <div style={{color:'#6b7280',fontSize:12,padding:'8px 0'}}>No active broadcasts.</div>
        )}
        {active.map(b => (
          <div key={b.id} data-testid={`broadcast-active-${b.id}`}
            style={{
              background:'#1f2937',border:`1px solid ${b.urgent?'#dc2626':'#059669'}`,borderRadius:8,
              padding:10,marginBottom:8
            }}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8,marginBottom:6}}>
              {b.urgent && (
                <span style={{background:'#dc2626',color:'white',fontSize:10,fontWeight:700,padding:'2px 6px',borderRadius:4}}>URGENT</span>
              )}
              <span style={{color:'#9ca3af',fontSize:11,marginLeft:'auto'}}>Sent {fmtTime(b.created_at)} EST</span>
            </div>
            <div style={{color:'white',fontSize:14,lineHeight:1.4,whiteSpace:'pre-wrap',marginBottom:8}}>{b.message}</div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:11,color:'#9ca3af'}}>
              <span>Expires {fmtTime(b.expires_at)} EST</span>
              <button
                data-testid={`broadcast-dismiss-${b.id}`}
                onClick={() => onDismiss(b.id)}
                style={{background:'transparent',border:'1px solid #4b5563',borderRadius:6,color:'#f3f4f6',fontSize:11,fontWeight:600,padding:'4px 10px',cursor:'pointer'}}
              >
                Dismiss
              </button>
            </div>
          </div>
        ))}

        {past.length > 0 && (
          <>
            <label style={{color:'#6b7280',fontSize:11,fontWeight:600,display:'block',marginTop:14,marginBottom:6}}>PAST ({past.length})</label>
            {past.slice(0, 10).map(b => (
              <div key={b.id} style={{background:'#111827',border:'1px solid #1f2937',borderRadius:8,padding:8,marginBottom:6,opacity:0.7}}>
                <div style={{color:'#9ca3af',fontSize:12,whiteSpace:'pre-wrap'}}>{b.message}</div>
                <div style={{color:'#4b5563',fontSize:10,marginTop:4}}>Sent {fmtTime(b.created_at)} EST</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}



function PlannedAbsencesPanel({
  absencesList, absencesLoading,
  query, setQuery, results, selected, setSelected,
  startDate, setStartDate, endDate, setEndDate,
  message, onSubmit, onDelete, today
}) {
  return (
    <div data-testid="planned-absences-panel">
      <p style={{color:'#9ca3af',fontSize:12,margin:'0 0 16px',lineHeight:1.4}}>
        Add a planned absence and the camper will be removed from the bus (AM and PM) on every day in the date range. A banner shows on the counselor app for each affected day.
      </p>

      {/* Camper search */}
      <label style={{color:'#9ca3af',fontSize:12,fontWeight:600,display:'block',marginBottom:4}}>CAMPER</label>
      {selected ? (
        <div data-testid="absence-selected-camper" style={{
          display:'flex',alignItems:'center',justifyContent:'space-between',
          padding:'10px 12px',background:'#1f2937',border:'1px solid #374151',borderRadius:8,marginBottom:12
        }}>
          <span style={{color:'white',fontSize:14,fontWeight:600}}>
            {selected.first_name} {selected.last_name}
            {selected.am_bus_number && <span style={{color:'#9ca3af',fontWeight:400,fontSize:12,marginLeft:8}}>AM: {selected.am_bus_number}</span>}
            {selected.pm_bus_number && <span style={{color:'#9ca3af',fontWeight:400,fontSize:12,marginLeft:8}}>PM: {selected.pm_bus_number}</span>}
          </span>
          <button data-testid="absence-clear-camper" onClick={() => { setSelected(null); setQuery(''); }}
            style={{background:'none',border:'none',color:'#9ca3af',cursor:'pointer',padding:4}}>
            <X size={16} />
          </button>
        </div>
      ) : (
        <div style={{position:'relative',marginBottom:12}}>
          <input
            data-testid="absence-camper-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type camper name..."
            style={{width:'100%',padding:10,background:'#1f2937',border:'1px solid #374151',borderRadius:8,color:'white',fontSize:14,boxSizing:'border-box'}}
          />
          {results.length > 0 && (
            <div data-testid="absence-camper-results" style={{
              position:'absolute',top:'100%',left:0,right:0,marginTop:4,
              background:'#1f2937',border:'1px solid #374151',borderRadius:8,
              maxHeight:240,overflowY:'auto',zIndex:10
            }}>
              {results.map(c => (
                <button
                  key={c.id || c._id}
                  data-testid={`absence-result-${c.id || c._id}`}
                  onClick={() => { setSelected(c); setQuery(''); }}
                  style={{
                    display:'block',width:'100%',textAlign:'left',
                    padding:'10px 12px',background:'none',border:'none',
                    borderBottom:'1px solid #374151',color:'white',fontSize:14,cursor:'pointer'
                  }}>
                  {c.first_name} {c.last_name}
                  <span style={{color:'#9ca3af',fontSize:12,marginLeft:8}}>
                    {c.am_bus_number || '—'} / {c.pm_bus_number || '—'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Date pickers */}
      <div style={{display:'flex',gap:8,marginBottom:12}}>
        <div style={{flex:1}}>
          <label style={{color:'#9ca3af',fontSize:12,fontWeight:600,display:'block',marginBottom:4}}>START DATE</label>
          <input
            data-testid="absence-start-date"
            type="date"
            value={startDate}
            min={today}
            onChange={(e) => setStartDate(e.target.value)}
            style={{width:'100%',padding:10,background:'#1f2937',border:'1px solid #374151',borderRadius:8,color:'white',fontSize:14,boxSizing:'border-box',colorScheme:'dark'}}
          />
        </div>
        <div style={{flex:1}}>
          <label style={{color:'#9ca3af',fontSize:12,fontWeight:600,display:'block',marginBottom:4}}>END DATE</label>
          <input
            data-testid="absence-end-date"
            type="date"
            value={endDate}
            min={startDate || today}
            onChange={(e) => setEndDate(e.target.value)}
            style={{width:'100%',padding:10,background:'#1f2937',border:'1px solid #374151',borderRadius:8,color:'white',fontSize:14,boxSizing:'border-box',colorScheme:'dark'}}
          />
        </div>
      </div>

      {message && (
        <div data-testid="absence-message" style={{
          marginBottom:12,padding:12,borderRadius:8,
          background:message.success?'#052e16':'#450a0a',
          color:message.success?'#4ade80':'#f87171',
          fontSize:14,fontWeight:500
        }}>{message.text}</div>
      )}

      <button
        data-testid="absence-save-btn"
        onClick={onSubmit}
        disabled={!selected || !startDate || !endDate}
        style={{
          width:'100%',padding:14,
          background:(!selected || !startDate || !endDate)?'#374151':'#dc2626',
          color:'white',fontSize:15,fontWeight:700,border:'none',borderRadius:10,
          cursor:(!selected || !startDate || !endDate)?'not-allowed':'pointer',
          marginBottom:24
        }}>
        Save Planned Absence
      </button>

      {/* Existing absences */}
      <label style={{color:'#9ca3af',fontSize:12,fontWeight:600,display:'block',marginBottom:8}}>
        ALL PLANNED ABSENCES ({absencesList.length})
      </label>
      {absencesLoading ? (
        <p style={{color:'#9ca3af',fontSize:13}}>Loading…</p>
      ) : absencesList.length === 0 ? (
        <p style={{color:'#6b7280',fontSize:13,fontStyle:'italic'}}>None scheduled.</p>
      ) : (
        <div data-testid="absences-list" style={{display:'flex',flexDirection:'column',gap:8}}>
          {absencesList.map(a => (
            <div key={a._id} data-testid={`absence-${a._id}`} style={{
              display:'flex',alignItems:'center',justifyContent:'space-between',
              padding:'10px 12px',background:'#1f2937',border:'1px solid #374151',borderRadius:8
            }}>
              <div>
                <div style={{color:'white',fontSize:14,fontWeight:600}}>
                  {a.first_name} {a.last_name}
                </div>
                <div style={{color:'#9ca3af',fontSize:12,marginTop:2}}>
                  {a.start_date} → {a.end_date}
                  {a.am_bus_number && <span style={{marginLeft:8}}>AM: {a.am_bus_number}</span>}
                  {a.pm_bus_number && <span style={{marginLeft:8}}>PM: {a.pm_bus_number}</span>}
                </div>
              </div>
              <button
                data-testid={`absence-delete-${a._id}`}
                onClick={() => onDelete(a._id)}
                style={{background:'#450a0a',border:'1px solid #7f1d1d',color:'#fca5a5',padding:'6px 10px',borderRadius:6,fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                <Trash2 size={12} /> Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
